/**
 * Responsible for displaying available characters in a textarea and limiting
 * the amount that can be submitted.
 * @module elements/fields/characterCounter
 */
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';
import random from '../../lib/random';
import stache from '../../lib/stache';

/**
 * Defines the default options for the a character counter
 * @type {Object}
 */
export const CHARACTER_COUNTER_OPTIONS = {
  fieldSelector: 'textarea',
  insertPosition: 'afterbegin',
  a11yCounterTemplate: '{{remaining}} {{remaining|pluralize:"character,characters"}} remaining',
};

/**
 * Defines the block name for the element injected into the DOM.
 * @type {string}
 */
export const BLOCK = 'character-counter';

/**
 * Defines the count element injected into the DOM.
 * @type {string}
 */
export const COUNT_ELEMENT = 'count';

/**
 * Defines the name of the block and count element.
 * @type {string}
 */
export const COUNT_BLOCK_ELEMENT = `${BLOCK}__${COUNT_ELEMENT}`;

/**
 * Defines an error block.
 * @type {string}
 */
export const COUNT_BLOCK_ERROR = `${BLOCK}--invalid`;

/**
 * Defines an error block element combo.
 * @type {string}
 */
export const COUNT_BLOCK_ELEMENT_ERROR = `${COUNT_BLOCK_ELEMENT}--invalid`;

/**
 * Defines the block selector
 * @type {string}
 */
export const WRAPPER_SELECTOR = `.${BLOCK}`;

/**
 * Defines the count block element selector
 * @type {string}
 */
export const COUNT_SELECTOR = `.${COUNT_BLOCK_ELEMENT}`;

/**
 * Updates an element with the text count in the textarea.
 * @param {HTMLElement} countEl - Element being updated
 * @returns {Function} When the event is fired off
 */
const makeUpdateCount = (countEl, a11yCountEl, getA11yCounter) => (e) => {
  const { length } = e.target.value;
  // eslint-disable-next-line no-param-reassign
  countEl.innerText = length;
  // eslint-disable-next-line no-param-reassign
  a11yCountEl.innerText = getA11yCounter(length);
};

/**
 * Responsible for making a set of elements valid.
 * @param {HTMLElement} wrapper - Container of character count
 * @param {HTMLElement} count - Element containing the count
 * @returns {Function} When the event is fired off
 */
const makeValid = (wrapper, count) => () => {
  wrapper.classList.remove(COUNT_BLOCK_ERROR);
  count.classList.remove(COUNT_BLOCK_ELEMENT_ERROR);
};

/**
 * Responsible for making a set of elements invalid.
 * @param {HTMLElement} wrapper - Container of character count
 * @param {HTMLElement} count - Element containing the count
 */
const makeInvalid = (wrapper, count) => () => {
  wrapper.classList.add(COUNT_BLOCK_ERROR);
  count.classList.add(COUNT_BLOCK_ELEMENT_ERROR);
};

/**
 * Creates the character counter markup used to keep track of character
 * counting.
 * @private
 * @param  {string} count - The amount of characters in the field
 * @param  {string} maxLength - The maximum length for the field
 * @param  {string} [className=''] - Additional className
 * @return {string} Fully formed HTML
 */
const createCharacterCounterComponent = ({ count, maxLength, id, a11yText, className = '' }) => `
  <span class="${BLOCK} ${className}">
    <span id="${id}" aria-live="polite" class="u-screen-reader">${a11yText}</span>
    <span class="${COUNT_BLOCK_ELEMENT}">${count}</span>
    / ${maxLength}
  </span>
`;

/**
 * Responsible for setup and eventually rendering a11y counter text.
 * @param {string} template - Template string
 * @param {number} length - The max characters allowed in the textarea
 * @return {Function} When called renders template
 */
const makeGetA11yCounter = (template, length) => count =>
  stache(template, { remaining: length - count });

/**
 * Responsible for either getting a field id if one does not already exist.
 * @param {HTMLElement} field - The field in question
 * @returns {string} A field id
 */
const resolveFieldId = (field) => {
  const ariaAttr = 'aria-describedby';
  const describedBy = field.getAttribute(ariaAttr);
  if (describedBy) {
    return describedBy;
  }

  const id = random({ prefix: 'character-counter' });
  field.setAttribute(ariaAttr, id);

  return id;
};

/**
 * Responsible for providing feedback to the user with the amount of characters
 * entered alongside the amount allowed. This is achieved by the following:
 *
 * - Read in the maxlength attribute (if not provided, throws an error since
 *   without it we don't know what to do)
 * - create some HTML that illustrates how many characters are in a given area,
 *   along with the maximum amount of characters
 *
 * From the DOM side, this can take in optional parameters in the form of
 * data attributes:
 *
 * - data-character-counter-add-class-name: allows for a custom class name to
 *   be added to the feedback mechanism created within.
 * - data-character-insert-position: the insert position based the element
 *   passed in using the position values found on insertAdjacentHTML. If this
 *   doesn't exist, it'll use the one provided on wiring, and then fallback to
 *   afterbegin.
 *
 * This happens when the field (an input or textarea) first loads as well as
 * when the user starts entering in characters. Here's an example of markup
 * using all options:
 *
 * @example
 * <div class="things"
 *      data-character-counter-add-class-name="yo-dawg"
 *      data-character-counter-insert-position="beforebegin">
 *   <textarea maxlength="100"></textarea>
 * </div>
 *
 * which will yield:
 *
 * @example
 * <div class="things"
 *      data-character-counter-add-class-name="yo-dawg"
 *      data-character-counter-insert-position="beforebegin">
 *   <span class="character-counter yo-dawg">
 *     <span class="character-counter__count">0</span> / 200
 *   </span>
 *   <textarea maxlength="100"></textarea>
 * </div>
 *
 * @param {HTMLElement} el - Contains the form field
 * @param {Object} options - Configurable options
 */
export default function characterCounter(el, options = {}, { deregister } = noopActions) {
  // The jQuery lookup is for PhantomJS because it's lame and doesn't know how
  // to read from el.dataset because it sucks at life. There, I said it.
  const {
    fieldSelector,
    insertPosition,
    characterCounterAddClassName: className = '',
    characterCounterInsertPosition: dataAttrInsertPosition,
    a11yCounterTemplate,
  } = Object.assign({}, CHARACTER_COUNTER_OPTIONS, options, el.dataset);
  const field = el.querySelector(fieldSelector);
  const maxLength = field.maxLength;
  const getA11yCounter = makeGetA11yCounter(a11yCounterTemplate, +maxLength);

  if (maxLength < 0) {
    throw new Error(`Character counter requires a max length on ${fieldSelector}`);
  }

  const fieldId = resolveFieldId(field);

  el.insertAdjacentHTML(dataAttrInsertPosition || insertPosition, createCharacterCounterComponent({
    className,
    count: field.value.length,
    maxLength,
    a11yText: getA11yCounter(field.value.length),
    id: fieldId,
  }));

  const wrapperEl = el.querySelector(WRAPPER_SELECTOR);
  const countEl = el.querySelector(COUNT_SELECTOR);
  const a11yCountEl = el.querySelector(`#${fieldId}`);

  const listeners = [
    {
      evt: 'input',
      handler: makeUpdateCount(countEl, a11yCountEl, getA11yCounter),
    },
    {
      evt: 'focus',
      handler: makeValid(wrapperEl, countEl),
    },
    {
      evt: 'invalid',
      handler: makeInvalid(wrapperEl, countEl),
    },
  ];

  listeners.forEach(({ evt, handler }) => {
    field.addEventListener(evt, handler);
  });

  return {
    refreshCounter: () => {
      const event = new Event("input");
      field.dispatchEvent(event);
    },
    removeListeners: () => {
      deregister();
      listeners.forEach(({ evt, handler }) => {
        field.removeEventListener(evt, handler);
      });
    },
  };
}

registerJQueryPlugin('characterCounter', characterCounter);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/character-counter.js